import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { ArrowRight } from 'lucide-react'
import { useReduceMotion } from '@/hooks/use-reduce-motion'
import { arrowClass, buttonClass } from '@/components/generals/button'
import SmartLink from '@/components/generals/smart-link'
import Champion from '@/components/landing/champion'
import Portrait, { TiltCard } from '@/components/landing/portrait'
import { about } from '@/constants/content'

interface AboutProps {
    showMoreLink?: boolean
    headingLevel?: 'h1' | 'h2'
}

// Meet David: the portrait swings into place as you scroll in, then the World Champion card.
const About: FC<AboutProps> = ({ showMoreLink = true, headingLevel = 'h2' }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const reduce = useReduceMotion()
    const Heading = headingLevel

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'start 25%'] })
    const portraitRotate = useTransform(scrollYProgress, [0, 1], [12, 3])
    const portraitX = useTransform(scrollYProgress, [0, 1], ['-18%', '0%'])

    return (
        <section ref={sectionRef} id='about' className='relative overflow-x-clip bg-paper py-24 sm:py-32 lg:py-40'>
            <div className='shell'>
                <div className='grid items-center gap-14 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <motion.div
                            style={reduce ? undefined : { rotate: portraitRotate, x: portraitX }}
                            className='mx-auto w-[min(100%,24rem)] lg:mx-0 lg:w-full lg:max-w-[28rem]'
                        >
                            <TiltCard>
                                <Portrait tone='cobalt' src='/images/david-mudry-web-designer.webp' />
                            </TiltCard>
                        </motion.div>
                    </div>

                    <div className='lg:col-span-6 lg:col-start-7'>
                        <Heading className='font-heading text-[16vw] font-bold leading-[0.86] tracking-[-0.015em] wdth-78 sm:text-[12vw] lg:text-[7.4vw] 2xl:text-[7rem]'>
                            <motion.span
                                className='inline-block origin-[70%_90%] text-blaze-deep'
                                whileHover={{ rotate: [0, -14, 10, -8, 0] }}
                                transition={{ duration: 0.7 }}
                            >
                                {about.heading.greeting}
                            </motion.span>

                            {' '}

                            {about.heading.name}
                        </Heading>

                        <p className='mt-8 max-w-[36rem] text-xl leading-relaxed text-ink/80 sm:text-2xl sm:leading-relaxed'>
                            {about.intro}
                        </p>
                    </div>
                </div>

                <div className='mt-20 lg:mt-28'>
                    <Champion />
                </div>

                {showMoreLink && (
                    <SmartLink
                        href='/about'
                        className={buttonClass({ variant: 'ink', size: 'lg', shadow: 'blaze', className: 'mt-12 w-full sm:w-auto' })}
                    >
                        {about.cta}
                        <ArrowRight aria-hidden='true' className={arrowClass} />
                    </SmartLink>
                )}
            </div>
        </section>
    )
}

export default About
